import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, AdminProfile } from '@/types'
import { api } from '@/lib/api'
import { saveTokens, clearTokens, getAccessToken, authStorage } from '@/utils/tokenService'

interface AuthState {
  user: User | null
  adminProfile: AdminProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  isRefreshingToken: boolean
}

interface AuthContextType extends AuthState {
  isSuperAdmin: boolean
  /** An agency's own admin — restricted to managing just their agency's counselors, nothing else in the portal. */
  isAgencyAdmin: boolean
  isCsm: boolean
  /** Module keys this admin may open, already resolved by the server. */
  moduleAccess: string[]
  /** Super admins always pass; everyone else is checked against the list. */
  hasModule: (moduleKey: string) => boolean
  // Email authentication (active)
  login: (email: string, otp: string) => Promise<boolean>
  // Phone authentication (commented for future use)
  // login: (phoneNumber: string, otp: string) => Promise<boolean>
  logout: (skipClearTokens?: boolean) => void
  refreshToken: () => Promise<boolean>
}

/** Does this /auth/profile or login response belong to someone allowed into this portal at all? */
const isPortalUser = (user: any): boolean =>
  user.userType === 'admin' ||
  user.userType === 'super_admin' ||
  user.userType === 'agency_admin' ||
  user.type === 'ADMIN' ||
  user.type === 'SUPER_ADMIN' ||
  user.type === 'AGENCY_ADMIN' ||
  (user.roles &&
    (user.roles.includes('admin') ||
      user.roles.includes('super_admin') ||
      user.roles.includes('agency_admin')))

/** 'AGENCY_ADMIN' / 'SUPER_ADMIN' takes precedence over the 'ADMIN' default. */
const resolveAdminType = (user: any): 'ADMIN' | 'SUPER_ADMIN' | 'AGENCY_ADMIN' => {
  if (user.userType === 'super_admin' || user.type === 'SUPER_ADMIN' || user.roles?.includes('super_admin')) {
    return 'SUPER_ADMIN'
  }
  if (user.userType === 'agency_admin' || user.type === 'AGENCY_ADMIN' || user.roles?.includes('agency_admin')) {
    return 'AGENCY_ADMIN'
  }
  return 'ADMIN'
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    adminProfile: null,
    isAuthenticated: false,
    isLoading: true,
    token: null,
    isRefreshingToken: false
  })

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = getAccessToken()
        if (token) {
          // Validate token with backend using correct endpoint
          try {
            const userData = await api.get<User>('/auth/profile')
            console.log('Auth verification response:', userData)
            console.log('User type check:', {
              userType: userData.userType,
              roles: userData.roles,
              type: userData.type
            })
            
            // Check if user is admin, super_admin or agency_admin (check multiple possible fields)
            const isAdmin = isPortalUser(userData)

            console.log('Is admin check result:', isAdmin)

            if (isAdmin) {
              // For admin users, the profile data is already in the response
              const adminProfile: AdminProfile = {
                id: userData.id,
                phoneNumber: userData.phoneNumber,
                name: userData.name,
                email: userData.email,
                isVerified: userData.isVerified,
                isActive: userData.isActive,
                type: resolveAdminType(userData),
                profileImage: userData.profileImage,
                createdAt: userData.createdAt,
                updatedAt: userData.updatedAt,
                profileCompletion: userData.profileCompletion?.percentage,
                lastLoginAt: userData.lastLoginAt
              }
              
              setAuthState({
                user: userData,
                adminProfile,
                isAuthenticated: true,
                isLoading: false,
                token,
                isRefreshingToken: false
              })
              console.log('Authentication successful, user set')
            } else {
              // Not an admin user, clear token
              console.log('Access denied. User type:', userData.userType, 'Type:', userData.type, 'Roles:', userData.roles)
              authStorage().removeItem('admin_auth_token')
              setAuthState(prev => ({
                ...prev,
                isLoading: false
              }))
            }
          } catch (error) {
            // Token is invalid, remove it
            console.error('Auth validation error:', error)
            authStorage().removeItem('admin_auth_token')
            setAuthState(prev => ({
              ...prev,
              isLoading: false
            }))
          }
        } else {
          setAuthState(prev => ({
            ...prev,
            isLoading: false
          }))
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        authStorage().removeItem('admin_auth_token')
        setAuthState(prev => ({
          ...prev,
          isLoading: false
        }))
      }
    }

    initializeAuth()
  }, [])

  // Listen for token refresh events
  useEffect(() => {
    const handleTokenRefresh = (event: CustomEvent) => {
      console.log("[AuthProvider] Token refresh event received")
      setAuthState(prev => ({ ...prev, isRefreshingToken: event.detail.isRefreshing }))
    }

    const handleTokenCleared = () => {
      console.log("[AuthProvider] Tokens cleared, logging out")
      logout(true)
    }

    window.addEventListener("tokenRefreshing" as any, handleTokenRefresh)
    window.addEventListener("tokensCleared" as any, handleTokenCleared)

    return () => {
      window.removeEventListener("tokenRefreshing" as any, handleTokenRefresh)
      window.removeEventListener("tokensCleared" as any, handleTokenCleared)
    }
  }, [authState.isRefreshingToken])

  // Email authentication (active)
  const login = async (email: string, otp: string): Promise<boolean> => {
    try {
      const data = await api.post<any>('/auth/verify-email-otp', { email: email.toLowerCase().trim(), otp })
      console.log('Login response:', data) // Debug log

      // For admins, backend auto-creates if not exists; no registrationToken expected

      // Handle response data
      const user = data.user || data
      const accessToken = data.accessToken || data.token
      const refreshToken = data.refreshToken

      // Only allow admin, super_admin or agency_admin users to access this portal
      const isAdmin = isPortalUser(user)

      if (!isAdmin) {
        console.error('Access denied. User type:', user.userType, 'Roles:', user.roles)
        throw new Error('Access denied. Only admin users can access this dashboard.')
      }

      // Check if user account is active
      if (!user.isActive) {
        console.error('Access denied. Account is inactive:', { userId: user.id, email: user.email })
        throw new Error('Account is inactive. Please contact support to activate your account.')
      }

      // Save both access and refresh tokens
      saveTokens({
        accessToken,
        refreshToken: refreshToken || accessToken // Fallback to access token if no refresh token
      })

      // Use the user data as admin profile
      const adminProfile: AdminProfile = {
        id: user.id,
        phoneNumber: user.phoneNumber,
        name: user.name,
        email: user.email,
        isVerified: user.isVerified,
        isActive: user.isActive,
        type: resolveAdminType(user),
        profileImage: user.profileImage,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        profileCompletion: user.profileCompletion?.percentage,
        lastLoginAt: user.lastLoginAt
      }

      setAuthState({
        user,
        adminProfile,
        isAuthenticated: true,
        isLoading: false,
        token: accessToken,
        isRefreshingToken: false
      })

      return true
    } catch (error) {
      console.error('Login error:', error)
      return false
    }
  }

  // Phone authentication (commented for future use)
  /*
  const login = async (phoneNumber: string, otp: string): Promise<boolean> => {
    try {
      const data = await api.post<any>('/auth/verify-otp', { phoneNumber, otp })
      console.log('Login response:', data) // Debug log

      // Handle response data
      const user = data.user || data
      const accessToken = data.accessToken || data.token
      const refreshToken = data.refreshToken

        // Only allow admin users to access this dashboard
        const isAdmin = user.userType === 'admin' ||
                       user.userType === 'super_admin' ||
                       (user.roles && (user.roles.includes('admin') || user.roles.includes('super_admin')))

        if (!isAdmin) {
          console.error('Access denied. User type:', user.userType, 'Roles:', user.roles)
          throw new Error('Access denied. Only admin users can access this dashboard.')
        }

        // Save both access and refresh tokens
        saveTokens({
          accessToken,
          refreshToken: refreshToken || accessToken // Fallback to access token if no refresh token
        })

        // Use the user data as admin profile
        const adminProfile: AdminProfile = {
          id: user.id,
          phoneNumber: user.phoneNumber,
          name: user.name,
          email: user.email,
          isVerified: user.isVerified,
          isActive: user.isActive,
          // Same fix as the initializeAuth path: check for super_admin
        // explicitly and default to ADMIN, rather than relying on operator
        // precedence that made any non-'admin' userType fall through to
        // SUPER_ADMIN.
        type: (user.userType === 'super_admin' ||
          user.type === 'SUPER_ADMIN' ||
          user.roles?.includes('super_admin')
          ? 'SUPER_ADMIN'
          : 'ADMIN') as 'ADMIN' | 'SUPER_ADMIN',
          profileImage: user.profileImage,
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          profileCompletion: user.profileCompletion?.percentage,
          lastLoginAt: user.lastLoginAt
        }

        setAuthState({
          user,
          adminProfile,
          isAuthenticated: true,
          isLoading: false,
          token: accessToken,
          isRefreshingToken: false
        })

        return true
    } catch (error) {
      console.error('Login error:', error)
      return false
    }
  }
  */

  const logout = (skipClearTokens = false) => {
    if (!skipClearTokens) {
      clearTokens()
    }
    setAuthState({
      user: null,
      adminProfile: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
      isRefreshingToken: false
    })
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      const currentToken = getAccessToken()
      if (!currentToken) return false

      // For now, just validate the current token instead of refreshing
      // The backend might not have a refresh endpoint yet
      await api.get('/auth/profile')
      return true
    } catch (error) {
      console.error('Token refresh error:', error)
      logout()
      return false
    }
  }

  const isSuperAdmin = authState.adminProfile?.type === 'SUPER_ADMIN'
  const isAgencyAdmin = authState.adminProfile?.type === 'AGENCY_ADMIN'
  const isCsm = Boolean(authState.user?.roles?.includes('csm'))

  // Server-resolved: it applies the default set for a never-configured admin,
  // so the portal never has to guess what "no configuration" means.
  const moduleAccess = authState.user?.effectiveModules ?? []

  const hasModule = (moduleKey: string) =>
    isSuperAdmin || moduleAccess.includes(moduleKey)

  const value: AuthContextType = {
    ...authState,
    isSuperAdmin,
    isAgencyAdmin,
    isCsm,
    moduleAccess,
    hasModule,
    login,
    logout,
    refreshToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}